import { Auth } from 'aws-amplify';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ResponseLogViewDto } from '../../services/api/requests/statusLogs/statusLogs';
import { websocketClient } from './websocketClient';

interface UseLogWebSocketOptions {
	onNewLog?: (logData: ResponseLogViewDto) => void;
	enabled?: boolean;
}

export const useLogWebSocket = (options: UseLogWebSocketOptions = {}) => {
	const { onNewLog, enabled = true } = options;
	const onNewLogRef = useRef(onNewLog);
	const [isConnected, setIsConnected] = useState(false);

	useEffect(() => {
		onNewLogRef.current = onNewLog;
	}, [onNewLog]);

	const connect = useCallback(async () => {
		if (!enabled) {
			return;
		}

		if (websocketClient.isConnected()) {
			setIsConnected(true);
			return;
		}

		try {
			const session = await Auth.currentSession();
			const token = session.getIdToken().getJwtToken();

			if (!token) {
				console.warn(
					'[WebSocket] No token available for WebSocket connection'
				);
				return;
			}

			const socket = websocketClient.connect(token);
			if (!socket) {
				console.error(
					'[WebSocket] Failed to create WebSocket connection'
				);
				return;
			}

			socket.on('connect', () => {
				setIsConnected(true);
			});

			socket.on('disconnect', () => {
				setIsConnected(false);
			});

			socket.on('new-log', (logData: ResponseLogViewDto) => {
				if (onNewLogRef.current) {
					onNewLogRef.current(logData);
				}
			});
		} catch (error) {
			console.error('[WebSocket] Error connecting to WebSocket:', error);
			setIsConnected(false);
		}
	}, [enabled]);

	const disconnect = useCallback(() => {
		websocketClient.disconnect();
		setIsConnected(false);
	}, []);

	useEffect(() => {
		if (enabled) {
			connect();
		} else {
			disconnect();
		}

		return () => {
			disconnect();
		};
	}, [enabled, connect, disconnect]);

	return {
		isConnected,
		connect,
		disconnect,
	};
};
